import { cn } from "@pma/utils/helpers"
import { ComponentPropsWithoutRef, createContext, forwardRef, useContext, useId } from "react"
import { FieldError } from "react-hook-form"

const FormItemContext = createContext<{ id: string; error?: FieldError }>({ id: "" })

export function FormItem({
  className,
  error,
  ...props
}: ComponentPropsWithoutRef<"div"> & { error?: FieldError }) {
  const id = useId()
  return (
    <FormItemContext.Provider value={{ id, error }}>
      <div className={cn("space-y-1", className)} {...props} />
    </FormItemContext.Provider>
  )
}

export const FormLabel = forwardRef<HTMLLabelElement, ComponentPropsWithoutRef<"label">>(
  ({ className, ...props }, ref) => {
    const { id, error } = useContext(FormItemContext)
    return (
      <label
        ref={ref}
        className={cn(
          "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          error && "text-red-500",
          className,
        )}
        htmlFor={id}
        {...props}
      />
    )
  },
)
FormLabel.displayName = "FormLabel"

export const FormControl = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<"input">>(
  ({ className, ...props }, ref) => {
    const { id, error } = useContext(FormItemContext)
    return (
      <input
        ref={ref}
        id={id}
        className={cn(
          "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-red-500 focus-visible:ring-red-500",
          className,
        )}
        {...props}
      />
    )
  },
)
FormControl.displayName = "FormControl"

export const FormMessage = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<"p">>(
  ({ className, children, ...props }, ref) => {
    const { error } = useContext(FormItemContext)
    if (!error) return null
    return (
      <p ref={ref} className={cn("text-sm font-medium text-red-500", className)} {...props}>
        {error.message}
      </p>
    )
  },
)
FormMessage.displayName = "FormMessage"

export const FormTextareaControl = forwardRef<
  HTMLTextAreaElement,
  ComponentPropsWithoutRef<"textarea">
>(({ className, ...props }, ref) => {
  const { id, error } = useContext(FormItemContext)

  return (
    <textarea
      ref={ref}
      id={id}
      className={cn(
        "flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        error && "border-red-500 focus-visible:ring-red-500",
        className,
      )}
      {...props}
    />
  )
})
FormTextareaControl.displayName = "FormTextareaControl"
